import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth, isAdmin } from '@/lib/auth';

/**
 * Guards every authenticated admin route. Three gates:
 *   1. /me responded with a user (otherwise → /sign-in)
 *   2. role is admin or superadmin (otherwise → /sign-in with an error)
 *   3. MFA was verified during this session (otherwise → /sign-in
 *      with `next` set to the current path so the user lands back here)
 */
export function AdminRoute() {
  const user = useAuth((s) => s.user);
  const loading = useAuth((s) => s.loading);
  const mfaVerified = useAuth((s) => s.mfaVerifiedThisSession);
  const hydrate = useAuth((s) => s.hydrate);
  const location = useLocation();

  useEffect(() => {
    if (user === null && !loading) {
      void hydrate();
    }
  }, [user, loading, hydrate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (user === null || !isAdmin(user) || !mfaVerified) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }

  return <Outlet />;
}
